import { useId, useRef, useState } from "react";
import {
  addCallerNumberBodySchema,
  e164Schema,
  type CallerNumberRow,
  type CallerNumbersResponse,
  type CallerVerification as Verification,
  type CallerVerificationState,
} from "@gioia365/contracts";
import { useQuery } from "@tanstack/react-query";
import { ApiRequestError } from "../../api/http";
import { TELEPHONY_REFRESH_MS } from "../../api/queries";
import { Button } from "../../design/components/Button";
import { Dialog } from "../../design/components/Dialog";
import { Reason } from "../../design/components/Reason";
import { TextField } from "../../design/components/TextField";
import { messageForError } from "../../i18n/errors";
import { useT, type Translate } from "../../i18n/locale";
import { formatPhoneForDisplay } from "../../leads/lead-display";
import type { TabContext } from "../../session/tab-context";
import { callerNumbersQuery, useAddCallerNumber, useVerifyCallerNumber } from "../../telephony/caller-number-queries";
import { CallerVerification, isVerifying } from "../../telephony/caller-verification";
import { withoutSeparators } from "../platform/allowed-numbers";

// «Aggiungi un cellulare» e «Verifica» di un cellulare del parco (piano M4, Task 17), per chi ha «Numeri per chiamare» (il Super
// admin e l'Admin dell'azienda, TEL-3 del 21/10/2026) e per il contesto di piattaforma: il server lo chiede al fornitore, che telefona al cellulare, e chi lo tiene digita il codice di sei cifre che compare qui
// (`CallerVerification`, lo stesso componente del numero mostrato della telefonia). Il numero si legge con le regole del
// pannello della telefonia (`withoutSeparators` e `e164Schema`), il nome con quelle del server; che sia un cellulare italiano
// lo dice il server (`caller_number_not_mobile`), come che il fornitore l'abbia già verificato per l'azienda
// (`caller_number_already_verified`: allora lo porta nel parco «Leggi i cellulari già verificati», e il motivo sta sul numero;
// per un cellulare già nel parco, alla «Verifica» di nuovo, prima lo si toglie dal parco, e la finestra lo dice). Troppe
// telefonate di verifica in un'ora (`429 rate_limited`) le dice con parole sue: aspettare un minuto non basta.
//
// Mentre la finestra è aperta e la verifica è chiesta, il parco si rilegge ogni 3 secondi (la conferma firmata del fornitore la
// scrive il server); a verifica confermata o non riuscita, a cellulare tolto da qualcun altro, o a finestra chiusa, smette. Il
// codice vive soltanto nello stato di questa finestra: le scritture non lo tengono (`gcTime: 0` e `reset()` subito), l'elenco
// del parco non lo ha, e chiusa la finestra non c'è più. Si vede soltanto finché la riga riletta ha la versione della risposta:
// confermata, non riuscita o chiesta di nuovo altrove, lo si dimentica.
//
// La finestra si chiude anche mentre la richiesta è in corso: un fornitore che non risponde non la blocca. La verifica, se
// parte, continua presso il fornitore, e «Verifica» sulla riga del parco chiede un codice nuovo; il codice di quella risposta non
// resta da nessuna parte (una scrittura senza osservatori esce dalla cache appena finisce, `gcTime: 0`).

type Props = {
  tab: TabContext;
  /** Il cellulare da verificare di nuovo (in verifica o non riuscito); `null` per aggiungerne uno. */
  existing: CallerNumberRow | null;
  onClose: () => void;
};

type Field = "number" | "label" | "request";
type Problem = { field: Field; text: (t: Translate) => string };

/** I rifiuti del server che riguardano il numero scritto: il motivo sta sul campo «Cellulare». */
const NUMBER_REFUSALS = ["caller_number_not_mobile", "caller_number_exists", "caller_number_already_verified"] as const;

/** Il motivo di un rifiuto della richiesta, con le parole della finestra per le verifiche troppo frequenti. */
const requestProblem = (tr: Translate, error: unknown) =>
  error instanceof ApiRequestError && error.code === "rate_limited" ? tr("callerNumbers.addDialog.tooMany") : messageForError(tr, error);

/** I campi di cui il server dice che non vanno (`422 validation_failed`, `details.fields`). */
function invalidFields(error: unknown): string[] {
  if (!(error instanceof ApiRequestError) || error.code !== "validation_failed") return [];
  const fields = error.details?.fields;
  return Array.isArray(fields) ? fields.filter((field): field is string => typeof field === "string") : [];
}

type Followed = { row: CallerNumberRow | null; gone: boolean };

/**
 * Il cellulare com'è adesso: la riga del parco riletto, se non è più vecchia di quella dell'ultima risposta (una rilettura
 * partita prima della richiesta non deve far tornare lo stato di prima), altrimenti quella della risposta. Se un parco letto
 * dopo la risposta (`readAt` dopo `knownAt`) non lo ha più, qualcun altro l'ha tolto: `gone`.
 */
function follow(pool: CallerNumbersResponse | null | undefined, readAt: number, known: CallerNumberRow | null, knownAt: number): Followed {
  if (known === null) return { row: null, gone: false };
  const listed = pool?.numbers.find((row) => row.id === known.id);
  if (listed === undefined) return pool && readAt > knownAt ? { row: null, gone: true } : { row: known, gone: false };
  return { row: listed.version >= known.version ? listed : known, gone: false };
}

/** Lo stato della verifica di un cellulare del parco; `null` se non si sa o se è stato tolto. */
function verificationOf(row: CallerNumberRow | null): CallerVerificationState | null {
  return row !== null && row.state !== "removed" ? row.state : null;
}

export function AddCallerNumberDialog({ tab, existing, onClose }: Props) {
  const t = useT();
  const baseId = useId();
  const add = useAddCallerNumber(tab);
  const verify = useVerifyCallerNumber(tab);
  const [text, setText] = useState("");
  const [label, setLabel] = useState("");
  // Il cellulare che la finestra segue: quello da verificare di nuovo, o quello appena aggiunto, e quando lo si è saputo (quello
  // da verificare di nuovo viene dal parco in cache, quindi ogni lettura del parco lo conosce).
  const [known, setKnown] = useState<CallerNumberRow | null>(existing);
  const [knownAt, setKnownAt] = useState(0);
  // Il codice e la versione del cellulare quando la risposta è arrivata.
  const [asked, setAsked] = useState<{ verification: Verification; version: number } | null>(null);
  const [problem, setProblem] = useState<Problem | null>(null);
  const numberRef = useRef<HTMLInputElement>(null);
  const labelRef = useRef<HTMLInputElement>(null);
  const pool = useQuery({
    ...callerNumbersQuery(tab),
    refetchInterval: (query) =>
      isVerifying(verificationOf(follow(query.state.data, query.state.dataUpdatedAt, known, knownAt).row)) ? TELEPHONY_REFRESH_MS : false,
  });
  const { row: current, gone } = follow(pool.data, pool.dataUpdatedAt, known, knownAt);
  const state = verificationOf(current);
  // Il codice vale per la richiesta che l'ha dato: confermata, non riuscita, chiesta di nuovo altrove (versione più nuova) o
  // tolta, si dimentica, e non riappare.
  if (asked !== null && (gone || (current !== null && current.version > asked.version))) setAsked(null);
  const instruction = asked !== null && current?.version === asked.version && state === "requested" ? asked.verification : null;
  const busy = add.isPending || verify.isPending;

  function fail(field: "number" | "label", message: (tr: Translate) => string) {
    setProblem({ field, text: message });
    (field === "number" ? numberRef : labelRef).current?.focus();
  }

  async function ask() {
    if (busy) return;
    if (known !== null) {
      setProblem(null);
      try {
        const answer = await verify.mutateAsync(known.id);
        verify.reset();
        setKnown(answer.number);
        setKnownAt(Date.now());
        setAsked({ verification: answer.verification, version: answer.number.version });
      } catch (error) {
        // Già verificato presso il fornitore: il numero è già nel parco, quindi l'importazione da sola non basta.
        const known = error instanceof ApiRequestError && error.code === "caller_number_already_verified";
        setProblem({ field: "request", text: (tr) => (known ? tr("callerNumbers.addDialog.alreadyVerified") : requestProblem(tr, error)) });
      }
      return;
    }
    const number = e164Schema.safeParse(withoutSeparators(text.trim()));
    if (!number.success) {
      fail("number", (tr) => tr("callerNumbers.addDialog.numberInvalid"));
      return;
    }
    const name = addCallerNumberBodySchema.shape.label.safeParse(label);
    if (!name.success) {
      fail("label", (tr) => tr("callerNumbers.addDialog.labelMissing"));
      return;
    }
    setProblem(null);
    try {
      const answer = await add.mutateAsync({ e164: number.data, label: name.data });
      add.reset();
      setKnown(answer.number);
      setKnownAt(Date.now());
      setAsked({ verification: answer.verification, version: answer.number.version });
    } catch (error) {
      const code = error instanceof ApiRequestError ? error.code : null;
      const fields = invalidFields(error);
      if (NUMBER_REFUSALS.some((refusal) => refusal === code)) fail("number", (tr) => messageForError(tr, error));
      else if (fields.includes("e164")) fail("number", (tr) => tr("callerNumbers.addDialog.numberInvalid"));
      else if (fields.includes("label")) fail("label", (tr) => tr("callerNumbers.addDialog.labelMissing"));
      else setProblem({ field: "request", text: (tr) => requestProblem(tr, error) });
    }
  }

  const errorOf = (field: Field) => (problem?.field === field ? problem.text(t) : undefined);
  const title = existing ? t("callerNumbers.addDialog.verifyTitle", { number: formatPhoneForDisplay(existing.e164) }) : t("callerNumbers.add");

  return (
    <Dialog
      open
      title={title}
      onClose={onClose}
      footer={<Button onClick={onClose}>{t("common.close")}</Button>}
    >
      <div className="g-stack">
        <p>{t("callerNumbers.addDialog.intro")}</p>
        <TextField
          id={`${baseId}-number`}
          ref={numberRef}
          label={t("callerNumbers.addDialog.number")}
          type="tel"
          value={known ? formatPhoneForDisplay(known.e164) : text}
          readOnly={known !== null}
          autoComplete="off"
          spellCheck={false}
          hint={known ? undefined : t("callerNumbers.addDialog.numberHint")}
          error={errorOf("number")}
          onChange={(event) => {
            setText(event.target.value);
            if (problem?.field === "number") setProblem(null);
          }}
        />
        {/* Un cellulare importato dal fornitore non ha un nome: allora il campo non c'è. */}
        {known === null || known.label !== null ? (
          <TextField
            id={`${baseId}-label`}
            ref={labelRef}
            label={t("callerNumbers.addDialog.label")}
            value={known ? (known.label ?? "") : label}
            readOnly={known !== null}
            maxLength={60}
            autoComplete="off"
            hint={known ? undefined : t("callerNumbers.addDialog.labelHint")}
            error={errorOf("label")}
            onChange={(event) => {
              setLabel(event.target.value);
              if (problem?.field === "label") setProblem(null);
            }}
          />
        ) : null}
        {gone ? (
          <Reason tone="warning" role="alert">
            {t("callerNumbers.addDialog.gone")}
          </Reason>
        ) : (
          <CallerVerification state={state} instruction={instruction} busy={busy} onVerify={() => void ask()} error={errorOf("request") ?? null} />
        )}
      </div>
    </Dialog>
  );
}
